import { useCallback, useState } from 'react';
import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  entityTypeLock,
  type EntitySearchState,
} from 'components/entity-search-modal';
import { api, failureMessage } from 'lib/request/api';
import type {
  EntitySearchEntityType,
  EntitySearchParams,
} from 'types/api-payloads';
import type {
  EntityResponse,
  EntitySearchQueryResult,
  SearchResult,
} from 'types/api-responses';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';

const ENTITY_QUERY_KEY = 'entities:show' as const;

/**
 * Reads one entity by its href.
 * @param {string} href - The entity's href, such as /entities/1
 * @return The queryOptions for that read
 */
const entityQueryOptions = (href: string) =>
  queryOptions({
    queryKey: [ENTITY_QUERY_KEY, href],
    queryFn: async ({ signal }) => {
      try {
        return await api
          .get(href.replace(/^\//, ''), { signal })
          .json<EntityResponse>();
      } catch (error) {
        throw new Error(await failureMessage(error), { cause: error });
      }
    },
  });

interface EntityRead {
  entity: EntityResponse | undefined;
  loading: boolean;
  error: string | null;
  getEntity: () => Promise<void>;
}

/**
 * The entity at an href, with a refetch for after an edit. The refetch clears
 * the entity first, so the tabs and panels it gates remount and reload theirs.
 * @param {string} href - The entity's href
 * @return {EntityRead} The entity, its read state and a refetch
 */
export function useEntity(href: string): EntityRead {
  const queryClient = useQueryClient();
  const { data, isFetching, error } = useQuery(entityQueryOptions(href));
  const getEntity = useCallback(
    () =>
      queryClient.resetQueries({
        queryKey: entityQueryOptions(href).queryKey,
      }),
    [queryClient, href]
  );

  return {
    entity: data,
    loading: isFetching,
    error: error ? error.message : null,
    getEntity,
  };
}

/**
 * The entity search modal's first search.
 * @param {EntitySearchEntityType[]} [entityTypes] The types it is locked to, if any.
 * @return {EntitySearchParams} The search.
 */
function initialPayload(
  entityTypes?: EntitySearchEntityType[]
): EntitySearchParams {
  return {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortAttribute: 'name',
    sortDirection: 'ASC',
    entityTypes,
    // Classification defaults match the Entities page: Primary + Aliases on.
    // Deduplicated is intentionally never enabled here — you can't dedupe an
    // already-deduplicated entity (the checkbox is also disabled in the UI).
    includePrimary: true,
    includeAliases: true,
  };
}

/**
 * An entity search. `opening` tells apart each opening of a type-locked modal,
 * which searches afresh even when its search is unchanged.
 * @param {EntitySearchParams} payload The search.
 * @param {number} opening The modal's type-locked opening.
 * @return {object} The query's options.
 */
function entitySearchQuery(payload: EntitySearchParams, opening: number) {
  return queryOptions({
    queryKey: ['entities', 'search-modal', payload, opening],
    queryFn: ({ signal }) =>
      api
        .post('queries/entities', { json: payload, signal })
        .json<SearchResult<EntitySearchQueryResult>>(),
    placeholderData: keepPreviousData,
  });
}

/**
 * The entity search modal's search, kept across openings. Each opening
 * re-applies the type lock, read at that moment, since the established survivor
 * type can change between openings. Takes the same types as the modal.
 * @param {boolean} isOpen Whether the modal is open.
 * @param {EntitySearchEntityType} [primaryEntityType] The viewed entity's type.
 * @param {EntitySearchEntityType[]} [allowedEntityTypes] The types it can still accept.
 * @return {EntitySearchState} The search, for the modal's `search`.
 */
export function useEntitySearch(
  isOpen: boolean,
  primaryEntityType?: EntitySearchEntityType,
  allowedEntityTypes?: EntitySearchEntityType[]
): EntitySearchState {
  const lockedEntityTypes = entityTypeLock(
    allowedEntityTypes,
    primaryEntityType
  );
  const [payload, setPayload] = useState(() =>
    initialPayload(lockedEntityTypes ?? undefined)
  );
  const [opening, setOpening] = useState(0);
  const [wasOpen, setWasOpen] = useState(false);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen && lockedEntityTypes) {
      setPayload((current) => ({
        ...current,
        page: 1,
        entityTypes: lockedEntityTypes,
      }));
      setOpening((count) => count + 1);
    }
  }

  const query = useQuery(entitySearchQuery(payload, opening));
  // A failed search keeps the last results listed.
  const [lastData, setLastData] = useState(query.data);
  if (query.data && query.data !== lastData) setLastData(query.data);

  return {
    result: {
      currentPayload: payload,
      loading: query.isFetching,
      data: query.data ?? lastData,
      error: query.error?.message,
    },
    setPayload,
    lockedEntityTypes,
  };
}
